import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, Collapse, GridLegacy as Grid, Link, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { LeadAgencyPicker } from "components/LeadAgencyPicker";
import { T2mAgencyAutocomplete } from "components/T2mAgencyAutocomplete";
import { STATES } from "encamp-shared/src/constants/states";
import { t2mStateHasLepcSelector } from "encamp-shared/src/constants/t2m-county-lepc-fd/getByState";
import { LeadAgencyPickerFragment, LepcInput } from "generated-graphql/graphql";
import { transform } from "hooks/transform/transformLepc";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, Controller } from "react-hook-form";
import { hasCriticalIssues } from "util/forms";

import { AgencyDueDateFields } from "../AgencyDueDateFields";
import { GET_LEPC, LEPC_SEARCH, UPSERT_LEPC, VALIDATE_LEPC_INPUT } from "./api";

function useLepcInputValidation(isCreate: boolean) {
  const [query] = useLazyQuery(VALIDATE_LEPC_INPUT, {
    fetchPolicy: "no-cache",
  });

  return useCallback(
    async (input: LepcInput) => {
      const { data } = await query({
        variables: {
          data: transform(input),
          id: !isCreate ? input.id : undefined,
        },
      });

      return data?.lepcInputValidator ?? [];
    },
    [query, isCreate]
  );
}

type LepcDialogProps = {
  onClose: () => void;
  mode?: "add" | "edit";
  lepcId?: string;
};

export const CreateEditLepcDialog: React.FC<LepcDialogProps> = ({ onClose, mode, lepcId }) => {
  const alerts = useAlerts();
  const [additionalFieldsOpen, setAdditionalFieldsOpen] = useState(false);

  const { data: agency, loading: isLoadingAgency } = useQuery(GET_LEPC, {
    fetchPolicy: "cache-and-network",
    variables: {
      id: lepcId ?? "",
    },
    skip: !lepcId,
  });

  const originalLeadAgency = useMemo(() => {
    return agency?.lepc.leadAgency ?? null;
  }, [agency]);

  const [selectedLeadAgency, setSelectedLeadAgency] = useState<LeadAgencyPickerFragment | null>(
    originalLeadAgency
  );

  useEffect(() => {
    setSelectedLeadAgency(originalLeadAgency);
  }, [originalLeadAgency]);

  const [upsertLepc, { loading: isUpserting }] = useMutation(UPSERT_LEPC, {
    refetchQueries: ["Lepc", LEPC_SEARCH],
  });

  const defaultValues: LepcInput = useMemo(
    () => ({
      id: agency?.lepc.id ?? undefined,
      name: agency?.lepc?.name ?? "",
      state: agency?.lepc?.state ?? "",
      city: agency?.lepc?.city ?? "",
      county: agency?.lepc?.county ?? "",
      zip: agency?.lepc?.zip ?? "",
      mailingAddress: agency?.lepc?.mailingAddress ?? "",
      mailingAddressLine2: agency?.lepc?.mailingAddressLine2 ?? "",
      contactName: agency?.lepc?.contactName ?? "",
      email: agency?.lepc?.email ?? "",
      phone: agency?.lepc?.phone ?? "",
      feeStructure: agency?.lepc?.feeStructure ?? "",
      latitude: agency?.lepc?.latitude ?? null,
      longitude: agency?.lepc?.longitude ?? null,
      leadAgencyName: agency?.lepc?.leadAgencyName ?? null,
      leadAgencyId: agency?.lepc?.leadAgencyId ?? null,
      paymentMethod: agency?.lepc?.paymentMethod ?? null,
      t2sValue: agency?.lepc?.t2sValue ?? null,
      submissionNotes: agency?.lepc?.submissionNotes ?? null,
      requirementDetails: agency?.lepc?.requirementDetails ?? null,
      additionalComments: agency?.lepc?.additionalComments ?? "",
      additionaRequirements: agency?.lepc?.additionaRequirements ?? "",
      additionalFees: agency?.lepc?.additionalFees ?? "",
      isEmailed: agency?.lepc?.isEmailed ?? false,
      isCurrent1AsOf2021: agency?.lepc?.isCurrent1AsOf2021 ?? false,
      isMailed: agency?.lepc?.isMailed ?? false,
      deactivated: agency?.lepc?.deactivated ?? false,
      isStateSubmits: agency?.lepc?.isStateSubmits ?? false,
      needsT2sFile: agency?.lepc?.needsT2sFile ?? false,
      isSubmittedViaPortal: agency?.lepc?.isSubmittedViaPortal ?? false,
      needsXmlZipFile: agency?.lepc?.needsXmlZipFile ?? false,
      portalId: agency?.lepc?.portalId ?? "",
      portalName: agency?.lepc?.portalName ?? "",
      dueDate: agency?.lepc?.dueDate ?? null,
      dueDateIsAnniversary: agency?.lepc?.dueDateIsAnniversary ?? false,
    }),
    [agency]
  );

  const form = useValidatingForm<LepcInput>(
    defaultValues,
    agency?.lepc.issues ?? [],
    useLepcInputValidation(mode === "add")
  );

  const {
    control,
    getValues,
    watch,
    setValue,
    issues,
    formState: { errors },
  } = form;

  const selectedState = watch("state");
  const isT2mWithLepcSelector = selectedState ? t2mStateHasLepcSelector(selectedState) : false;

  const onSubmit = async () => {
    const input = {
      ...getValues(),
    };

    await upsertLepc({
      variables: {
        id: lepcId,
        input: transform(input),
      },
      onCompleted: (data) => {
        onClose();
        alerts.success("LEPC saved successfully");
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while saving the LEPC");
      },
    });
  };

  const savingOrLoading = isLoadingAgency || isUpserting;
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      title={`${mode === "add" ? "Add" : "Edit"} LEPC`}
      primaryActionButtonProps={{
        loading: savingOrLoading,
        onClick: onSubmit,
        disabled: hasCriticalIssues(issues) || !form.formState.isDirty,
      }}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
    >
      <FormProvider {...form}>
        <Box
          component="form"
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            alignItems: "center",
            width: 500,
          }}
        >
          <Grid container spacing={4} justifyContent="center" pt={1}>
            <Grid item xs={12}>
              {mode === "edit" && lepcId && (
                <Box sx={{ mb: 2 }}>
                  <Link
                    href={`/staff/fulfillment/facilities?omnisearch=lepcId%3A${lepcId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                  >
                    {agency?.lepc.facilityCount ?? 0} associated facilities
                    <OpenInNew sx={{ fontSize: 16 }} />
                  </Link>
                </Box>
              )}
              <FormTextField
                name="name"
                label="Name"
                control={control}
                textFieldProps={{ required: true }}
              />
              <FormTextField name="mailingAddress" label="Mailing Address" control={control} />
              <FormTextField
                name="mailingAddressLine2"
                label="Mailing Address Line 2"
                control={control}
              />
              <FormTextField name="city" label="City" control={control} />
              <FormTextField name="county" label="County" control={control} />
              <Grid item container xs={12} spacing={1}>
                <Grid item xs={6}>
                  <FormSelect
                    name="state"
                    label="State/Territory"
                    control={control}
                    selectItems={STATES.map((state) => ({
                      value: state,
                      display: state,
                    }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormTextField name="zip" label="Zip" control={control} />
                </Grid>
              </Grid>
              <FormTextField name="email" label="Email" control={control} />
              <FormTextField
                name="additionalComments"
                label="Additional Comments"
                control={control}
                textFieldProps={{ multiline: true }}
              />
              {selectedState === "CA" && (
                <AgencyDueDateFields control={control} watch={watch} setValue={setValue} />
              )}

              <Typography variant="subtitle1" gutterBottom color="text.secondary">
                Submission Method
              </Typography>
              <CheckboxField name="isEmailed" label="Is Emailed" control={control} noErrorDisplay />
              <CheckboxField name="isMailed" label="Is Mailed" control={control} noErrorDisplay />
              <CheckboxField
                name="isSubmittedViaPortal"
                label="Is submitted via portal"
                control={control}
                noErrorDisplay
              />
              <CheckboxField
                name="isStateSubmits"
                label="Is submitted by the state"
                control={control}
                noErrorDisplay
              />

              <ErrorDisplay
                errors={errors}
                fields={[
                  "isEmailed",
                  // These are commented out because they all share
                  // essentially the same error message, and it's more
                  // clear if the message is only listed once
                  // -  "isMailed",
                  // -  "isSubmittedViaPortal",
                  // -  "isStateSubmits",
                ]}
              />

              <CheckboxField
                name="deactivated"
                label="Deactivated"
                control={control}
                noErrorDisplay
              />
              <CheckboxField
                name="needsT2sFile"
                label="Needs T2s File"
                control={control}
                noErrorDisplay
              />
              <CheckboxField
                name="needsXmlZipFile"
                label="Needs XML Zip File"
                control={control}
                noErrorDisplay
              />

              {/* T2M Portal fields - shown by default for T2M states with LEPC selector */}
              {isT2mWithLepcSelector && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    T2M Portal Information
                  </Typography>
                  <T2mAgencyAutocomplete
                    agencyType="lepc"
                    state={selectedState}
                    county={watch("county")}
                    portalId={watch("portalId")}
                    portalName={watch("portalName")}
                    onSelect={(portalId, portalName) => {
                      setValue("portalId", portalId, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      setValue("portalName", portalName, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    errors={errors}
                  />
                </Box>
              )}

              <Typography
                onClick={() => setAdditionalFieldsOpen((prev) => !prev)}
                sx={{ cursor: "pointer" }}
                my={3}
                color="primary"
                gutterBottom
              >
                Show Additional Fields
              </Typography>
              <Collapse in={additionalFieldsOpen} timeout="auto" unmountOnExit>
                <FormTextField name="contactName" label="Contact Name" control={control} />
                <FormTextField name="phone" label="Phone" control={control} />
                <FormTextField name="feeStructure" label="Fee Structure" control={control} />
                <FormTextField name="latitude" label="Latitude" control={control} />
                <FormTextField name="longitude" label="Longitude" control={control} />

                <Controller
                  name="leadAgencyId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <LeadAgencyPicker
                      {...field}
                      {...fieldState}
                      onChange={(value) => {
                        field.onChange(value?.id);
                        setSelectedLeadAgency(value);
                      }}
                      displayAgencyLink
                      value={selectedLeadAgency}
                    />
                  )}
                />
                <FormTextField name="paymentMethod" label="Payment Method" control={control} />
                <FormTextField name="t2sValue" label="T2s Value" control={control} />
                <FormTextField
                  name="submissionNotes"
                  label="Submission Notes"
                  control={control}
                  textFieldProps={{ multiline: true }}
                />
                <FormTextField
                  name="requirementDetails"
                  label="Requirement Details"
                  control={control}
                />
                <FormTextField
                  name="additionaRequirements"
                  label="Additional Requirements"
                  control={control}
                />
                <FormTextField name="additionalFees" label="Additional Fees" control={control} />
                <CheckboxField
                  name="isCurrent1AsOf2021"
                  label="Is Current 1 as of 2021"
                  control={control}
                  noErrorDisplay
                />
                {/* Portal fields - only in Additional Fields for non-T2M states */}
                {!isT2mWithLepcSelector && (
                  <>
                    <FormTextField name="portalId" label="Portal Id" control={control} />
                    <FormTextField name="portalName" label="Portal Name" control={control} />
                  </>
                )}
              </Collapse>
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </Dialog>
  );
};
